import { useCallback } from 'react';
import type { FieldValues, SubmitErrorHandler, UseFormHandleSubmit } from 'react-hook-form';
import { captureFormRejected, type FormKey } from './formAnalytics';

/**
 * An `onInvalid` that sends the refused submit to analytics as `form`, with the top-level keys of the fields that
 * failed and never their values, then hands the errors on to `onInvalid`.
 */
export function rejectedSubmitHandler<T extends FieldValues>(
    form: FormKey,
    onInvalid?: SubmitErrorHandler<T>
): SubmitErrorHandler<T> {
    return (errors, event) => {
        captureFormRejected({ form, invalidFields: Object.keys(errors) });
        return onInvalid?.(errors, event);
    };
}

/**
 * The form's `handleSubmit`, sending each submit its validation refuses to analytics as `form`; a call site's own
 * `onInvalid` still runs. Hand it to `useCreateDraft` in place of the form's own, so a refused draft save counts too.
 */
export function useRejectedSubmitCapture<T extends FieldValues>(
    handleSubmit: UseFormHandleSubmit<T>,
    form: FormKey
): UseFormHandleSubmit<T> {
    return useCallback(
        (onValid, onInvalid) => handleSubmit(onValid, rejectedSubmitHandler(form, onInvalid)),
        [handleSubmit, form]
    );
}
